import * as adminUserApi from '@apis/admin-user';
import * as adminUserContentsApi from '@apis/admin-user-contents';
import * as chatsApi from '@apis/chats';
import { UserContentDetailCard } from '@components/user-contents/UserContentDetailCard';
import { UserIssuedAffiliatesSection } from '@components/user/userDetail/UserIssuedAffiliatesSection';
import { AffiliateAttribution } from '@models/affiliate';
import { ExtendedLegacyChatRoom } from '@models/chat';
import { UserContent } from '@models/user-contents';
import { AdminUserFavorite } from '@models/user-favorite';
import { useQueries, useQuery } from '@tanstack/react-query';
import { Button, Card, Modal, Spin, Table, Tabs, Tag, Tooltip } from 'antd';
import { toast } from 'react-toastify';
import { ColumnsType } from 'antd/es/table';
import * as cheerio from 'cheerio';
import moment from 'moment';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useMemo, useState } from 'react';

interface UserActivityTabProps {
  userId?: string;
}

const formatDate = (date?: Date | string | null) => (date ? moment(date).format('YYYY-MM-DD HH:mm') : '-');
const formatNumber = (n?: number | null) => (typeof n === 'number' ? n.toLocaleString() : '-');

const TableTotalCount: React.FC<{ total?: number; loading?: boolean }> = ({ total, loading }) => (
  <div style={{ marginBottom: 8, fontSize: 12, color: '#666' }}>총 {loading ? '-' : formatNumber(total ?? 0)}건</div>
);

const copyToastBodyStyle = {
  color: 'var(--White-90, rgba(255, 255, 255, 0.90))',
  textAlign: 'center' as const,
  fontFamily: 'Pretendard',
  fontSize: '14px',
  fontStyle: 'normal' as const,
  fontWeight: 500,
  lineHeight: '20px',
};

const getAttributionTypeLabel = (attributionType?: string) => {
  if (attributionType === 'campaign_visit_reward') {
    return '캠페인';
  }
  return '상품';
};

const getReviewAverage = (record: UserContent) => {
  const total =
    (record.rates?.rate1 || 0) + (record.rates?.rate2 || 0) + (record.rates?.rate3 || 0) + (record.rates?.rate4 || 0);
  return Number((total / 4).toFixed(2));
};

const getUserContentPlainText = (html?: string, maxLength = 120): string => {
  if (!html) return '';
  const dom = cheerio.load(html);
  const title = dom('h1').text();
  dom('h1').remove();
  const paragraphs: string[] = [];
  dom('p').each((_, element) => {
    paragraphs.push(dom(element).text());
  });
  const description = paragraphs.join(' ').trim();
  return [title, description].filter(Boolean).join(' ').slice(0, maxLength);
};

const BOARD_OPTIONS = [
  { value: 'review', label: '후기' },
  { value: 'curation', label: '큐레이션' },
  { value: 'post', label: '게시글' },
  { value: 'vote', label: '투표' },
];

const STATUS_COLORS: Record<string, string> = {
  open: 'green',
  pending: 'orange',
  hidden: 'red',
  delist: 'red',
  end: 'default',
  reserved: 'blue',
  suggestion: 'purple',
};

const FAVORITE_TYPE_LABELS: Record<string, string> = {
  product: '상품',
  partner: '파트너',
  'user-content': '컨텐츠',
  comment: '댓글',
};

const getFavoriteTypeLabel = (favorite: AdminUserFavorite): string => {
  const type = favorite.type || (favorite.productId ? 'product' : undefined);
  if (type && FAVORITE_TYPE_LABELS[type]) {
    return FAVORITE_TYPE_LABELS[type];
  }
  if (favorite.userContentId) return FAVORITE_TYPE_LABELS['user-content'];
  if (favorite.commentId) return FAVORITE_TYPE_LABELS.comment;
  if (favorite.partnerId) return FAVORITE_TYPE_LABELS.partner;
  if (favorite.productId) return FAVORITE_TYPE_LABELS.product;
  return '-';
};

const getFavoriteTargetId = (favorite: AdminUserFavorite): string | undefined => {
  return favorite.targetId || favorite.productId || favorite.userContentId || favorite.partnerId || favorite.commentId;
};

const ContentsSection: React.FC<{ userId: string }> = ({ userId }) => {
  const [board, setBoard] = useState('review');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [showContent, setShowContent] = useState<React.ReactNode>();

  const query = useQuery({
    queryKey: [`user-detail-contents-${userId}`, board, page, size],
    queryFn: () =>
      adminUserContentsApi.searchUserContents({
        keyword: userId,
        board,
        page,
        size,
      } as any),
    enabled: !!userId,
  });

  const boardCountQueries = useQueries({
    queries: BOARD_OPTIONS.map((opt) => ({
      queryKey: [`user-detail-contents-count-${userId}`, opt.value],
      queryFn: () =>
        adminUserContentsApi.searchUserContents({
          keyword: userId,
          board: opt.value,
          page: 0,
          size: 1,
        } as any),
      enabled: !!userId,
      select: (response: Awaited<ReturnType<typeof adminUserContentsApi.searchUserContents>>) =>
        response.data?.total ?? 0,
    })),
  });

  const boardTotalByKey = useMemo(() => {
    return BOARD_OPTIONS.reduce<Record<string, number>>((acc, opt, index) => {
      acc[opt.value] = boardCountQueries[index]?.data ?? 0;
      return acc;
    }, {});
  }, [boardCountQueries]);

  const result = query.data?.data;
  const contents: UserContent[] = result?.contents || [];

  const columns: ColumnsType<UserContent> = useMemo(() => {
    const cols: ColumnsType<UserContent> = [
      {
        title: 'ID',
        width: 80,
        render: (record) => record._id,
      },
      {
        title: '제목 / 내용',
        width: 400,
        render: (record: UserContent) => (
          <div style={{ maxWidth: '400px' }}>
            {record.title && <div style={{ fontWeight: 500 }}>{record.title}</div>}
            <div
              style={{
                fontSize: 11,
                color: '#888',
                maxHeight: 36,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
              {getUserContentPlainText(record.text)}
            </div>
          </div>
        ),
      },
    ];

    if (board === 'review') {
      cols.push({
        title: '평점',
        width: 80,
        render: (record: UserContent) => (
          <Tooltip
            overlay={
              <table>
                <tbody>
                  <tr>
                    <td>친화력</td>
                    <td>전문성</td>
                    <td>준비성</td>
                    <td>시간준수</td>
                    <td>아이 만족도</td>
                  </tr>
                  <tr>
                    <td>{record.rates?.rate1 ?? '-'}</td>
                    <td>{record.rates?.rate2 ?? '-'}</td>
                    <td>{record.rates?.rate3 ?? '-'}</td>
                    <td>{record.rates?.rate4 ?? '-'}</td>
                    <td>{record.rates?.rate5 ?? '-'}</td>
                  </tr>
                </tbody>
              </table>
            }>
            <span>{getReviewAverage(record)}</span>
          </Tooltip>
        ),
      });
    }

    cols.push(
      {
        title: '상태',
        dataIndex: 'status',
        width: 80,
        render: (status) => <Tag color={STATUS_COLORS[status] || 'default'}>{status}</Tag>,
      },
      {
        title: '작성일',
        dataIndex: 'createdAt',
        width: 130,
        render: (date) => formatDate(date),
      },
    );

    if (board === 'review') {
      cols.push({
        title: '보기',
        width: 80,
        align: 'center',
        render: (record: UserContent) => (
          <Button
            size="small"
            onClick={() => {
              setShowContent(<UserContentDetailCard userContent={record} />);
            }}>
            보기
          </Button>
        ),
      });
    }

    return cols;
  }, [board]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Tabs
        size="small"
        activeKey={board}
        onChange={(key) => {
          setBoard(key);
          setPage(0);
        }}
        items={BOARD_OPTIONS.map((opt, index) => {
          const countQuery = boardCountQueries[index];
          const countLabel = countQuery?.isLoading ? '' : ` (${formatNumber(boardTotalByKey[opt.value])})`;
          return { key: opt.value, label: `${opt.label}${countLabel}` };
        })}
      />
      {query.isLoading ? (
        <div style={{ padding: 30, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        <Table<UserContent>
          rowKey={(record) => `${record._id}`}
          columns={columns}
          dataSource={contents}
          size="small"
          pagination={{
            current: page + 1,
            pageSize: size,
            total: result?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            onChange: (nextPage, nextSize) => {
              setPage((nextPage || 1) - 1);
              setSize(nextSize || 10);
            },
          }}
        />
      )}
      <Modal
        open={!!showContent}
        onOk={() => {
          setShowContent(undefined);
        }}
        onCancel={() => {
          setShowContent(undefined);
        }}>
        <div style={{ height: '36px' }}></div>
        <OverlayScrollbarsComponent
          defer
          options={{
            scrollbars: { autoHide: 'scroll' },
            overflow: {
              x: 'hidden',
            },
          }}
          style={{
            backgroundColor: '#ffffff',
            marginTop: 0,
            position: 'relative',
            overflow: 'auto',
            width: '100%',
            height: 'calc(100dvh - 280px)',
          }}>
          {showContent}
        </OverlayScrollbarsComponent>
      </Modal>
    </div>
  );
};

const FavoritesSection: React.FC<{ userId: string }> = ({ userId }) => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const query = useQuery({
    queryKey: [`user-detail-favorites-${userId}`, page, size],
    queryFn: () => adminUserApi.getUserFavorites(userId, { page, size }),
    enabled: !!userId,
  });

  const result = query.data?.data;
  const favorites: AdminUserFavorite[] = result?.contents || [];

  const columns: ColumnsType<AdminUserFavorite> = [
    {
      title: '타입',
      dataIndex: 'type',
      width: 100,
      render: (_, record) => <Tag>{getFavoriteTypeLabel(record)}</Tag>,
    },
    {
      title: '판매/발행자',
      width: 120,
      render: (record: AdminUserFavorite) => record.sellerOrPublisherName || '-',
    },
    {
      title: '대상',
      width: 200,
      render: (record: AdminUserFavorite) => record.targetTitle || '-',
    },
    {
      title: '대상 ID',
      width: 100,
      render: (record: AdminUserFavorite) => {
        const targetId = getFavoriteTargetId(record);
        return (
          <Button
            size="small"
            disabled={!targetId}
            onClick={() => {
              navigator.clipboard.writeText(targetId || '');
              toast.dark('대상 ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                bodyStyle: copyToastBodyStyle,
                theme: 'dark',
              });
            }}>
            ID 복사
          </Button>
        );
      },
    },
    {
      title: '판매 타입',
      dataIndex: 'saleType',
      width: 100,
      render: (s) => s || '-',
    },
    {
      title: '찜한 일시',
      dataIndex: 'createdAt',
      width: 150,
      render: (d) => formatDate(d),
    },
  ];

  return query.isLoading ? (
    <div style={{ padding: 30, textAlign: 'center' }}>
      <Spin />
    </div>
  ) : (
    <>
      <TableTotalCount total={result?.total} />
      <Table<AdminUserFavorite>
        rowKey={(record) => `${record._id}`}
        columns={columns}
        dataSource={favorites}
        size="small"
        pagination={{
          current: page + 1,
          pageSize: size,
          total: result?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          onChange: (nextPage, nextSize) => {
            setPage((nextPage || 1) - 1);
            setSize(nextSize || 10);
          },
        }}
      />
    </>
  );
};

const ChatsSection: React.FC<{ userId: string }> = ({ userId }) => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const query = useQuery({
    queryKey: [`user-detail-chats-${userId}`, page, size],
    // Legacy chatlist is retrieved from `chat-room-legacy` schema via `/admin/v1/chats` (deprecated).
    // `keyword` supports matching `parentId` / `tutorId` directly (userId is stored as string there).
    queryFn: () => chatsApi.searchChatRooms({ keyword: userId, page, size } as any),
    enabled: !!userId,
  });

  const result = query.data?.data;
  const rooms: ExtendedLegacyChatRoom[] = result?.contents || [];

  const columns: ColumnsType<ExtendedLegacyChatRoom> = [
    {
      title: '클래스',
      width: 180,
      render: (record) => {
        const className = (record.class as any)?.name || (record.class as any)?.classTitle || record.classId;
        return <div style={{ fontSize: 12 }}>{className || '-'}</div>;
      },
    },
    {
      title: '최근 메시지',
      width: 260,
      render: (record) => (
        <div
          style={{
            fontSize: 11,
            maxHeight: 36,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
          {record.chattext || '-'}
        </div>
      ),
    },
    {
      title: '최근 메시지 일시',
      width: 170,
      render: (record) => {
        const parentAt = record.parentLastMessageAt;
        const tutorAt = record.tutorLastMessageAt;
        if (!parentAt && !tutorAt) return '-';
        if (parentAt && tutorAt) {
          return moment(parentAt).valueOf() >= moment(tutorAt).valueOf() ? formatDate(parentAt) : formatDate(tutorAt);
        }
        return formatDate(parentAt || tutorAt);
      },
    },
    {
      title: '안읽음(부모/튜터)',
      width: 150,
      render: (record) => (
        <span style={{ fontSize: 12, color: '#888' }}>
          {record.unreadParentCount ?? 0} / {record.unreadTutorCount ?? 0}
        </span>
      ),
    },
  ];

  return query.isLoading ? (
    <div style={{ padding: 30, textAlign: 'center' }}>
      <Spin />
    </div>
  ) : (
    <>
      <TableTotalCount total={result?.total} />
      <Table<ExtendedLegacyChatRoom>
        rowKey={(record) => `${record._id}`}
        columns={columns}
        dataSource={rooms}
        size="small"
        pagination={{
          current: page + 1,
          pageSize: size,
          total: result?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          onChange: (nextPage, nextSize) => {
            setPage((nextPage || 1) - 1);
            setSize(nextSize || 10);
          },
        }}
      />
    </>
  );
};

const AffiliatesSection: React.FC<{ userId: string }> = ({ userId }) => {
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);

  const query = useQuery({
    queryKey: [`user-detail-affiliates-${userId}`, page, size],
    queryFn: () => adminUserApi.getUserAffiliateAttributions(userId, { page, size }),
    enabled: !!userId,
  });

  const result = query.data?.data;
  const items: AffiliateAttribution[] = result?.contents || [];

  const columns: ColumnsType<AffiliateAttribution> = [
    {
      title: '타입',
      dataIndex: 'attributionType',
      width: 80,
      render: (t) => <Tag>{getAttributionTypeLabel(t)}</Tag>,
    },
    {
      title: '발행자',
      width: 120,
      render: (record: AffiliateAttribution) => {
        const issuer = record.issuer;
        const issuerUserId = record.issuerUserId || issuer?.id;
        const issuerName = issuer?.profile?.name || issuer?.profile?.nickname || issuer?.profile?.realName || '-';
        return (
          <div>
            <div>{issuerName}</div>
            <Button
              size="small"
              disabled={!issuerUserId}
              onClick={() => {
                navigator.clipboard.writeText(issuerUserId || '');
                toast.dark('발행자 ID를 클립보드에 복사했습니다', {
                  position: 'top-center',
                  autoClose: 3000,
                  bodyStyle: copyToastBodyStyle,
                  theme: 'dark',
                });
              }}>
              ID 복사
            </Button>
          </div>
        );
      },
    },
    {
      title: '어필리에이트 ID',
      width: 100,
      render: (_, record) => (
        <Button
          size="small"
          disabled={!record.affiliateId}
          onClick={() => {
            navigator.clipboard.writeText(record.affiliateId || '');
            toast.dark('어필리에이트 ID를 클립보드에 복사했습니다', {
              position: 'top-center',
              autoClose: 3000,
              bodyStyle: copyToastBodyStyle,
              theme: 'dark',
            });
          }}>
          ID 복사
        </Button>
      ),
    },
    {
      title: '대상 ID(상품/캠페인)',
      width: 100,
      render: (_, record) => {
        const targetId = record.targetId || record.productId;
        return (
          <Button
            size="small"
            disabled={!targetId}
            onClick={() => {
              navigator.clipboard.writeText(targetId || '');
              toast.dark('대상 ID를 클립보드에 복사했습니다', {
                position: 'top-center',
                autoClose: 3000,
                bodyStyle: copyToastBodyStyle,
                theme: 'dark',
              });
            }}>
            ID 복사
          </Button>
        );
      },
    },
    {
      title: '상품/캠페인명',
      width: 200,
      render: (_, record) => <span style={{ fontSize: 11 }}>{record.targetName || record.productName || '-'}</span>,
    },
    {
      title: '결제 금액',
      dataIndex: 'paymentAmount',
      width: 100,
      align: 'right',
      render: (amount) => formatNumber(amount),
    },
    {
      title: '적립 포인트',
      dataIndex: 'pointAmount',
      width: 100,
      align: 'right',
      render: (amount) => formatNumber(amount),
    },
    {
      title: '적립 여부',
      dataIndex: 'pointAccumulated',
      width: 90,
      align: 'center',
      render: (accumulated) => (accumulated ? <Tag color="green">적립</Tag> : <Tag>대기</Tag>),
    },
    {
      title: '환불',
      dataIndex: 'isRefunded',
      width: 70,
      align: 'center',
      render: (refunded) => (refunded ? <Tag color="red">환불</Tag> : '-'),
    },
    {
      title: '방문일시',
      dataIndex: 'visitedAt',
      width: 150,
      render: (d) => formatDate(d),
    },
  ];

  return query.isLoading ? (
    <div style={{ padding: 30, textAlign: 'center' }}>
      <Spin />
    </div>
  ) : (
    <>
      <TableTotalCount total={result?.total} />
      <Table<AffiliateAttribution>
        rowKey={(record) => `${record._id}`}
        columns={columns}
        dataSource={items}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page + 1,
          pageSize: size,
          total: result?.total || 0,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
          onChange: (nextPage, nextSize) => {
            setPage((nextPage || 1) - 1);
            setSize(nextSize || 10);
          },
        }}
      />
    </>
  );
};

export const UserActivityTab: React.FC<UserActivityTabProps> = ({ userId }) => {
  if (!userId) {
    return null;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Card title="작성 콘텐츠" size="small">
        <ContentsSection userId={userId} />
      </Card>

      <Card title="찜 목록" size="small">
        <FavoritesSection userId={userId} />
      </Card>

      <Card title="채팅방" size="small">
        <ChatsSection userId={userId} />
      </Card>

      <Card title="어필리에이트 (발행 기준)" size="small">
        <UserIssuedAffiliatesSection userId={userId} />
      </Card>

      <Card title="어필리에이트 (방문 기준)" size="small">
        <AffiliatesSection userId={userId} />
      </Card>
    </div>
  );
};
